import type { Deviation, DeviationKind, Thresholds } from "../contract/types";

/**
 * « Hors tolérance » sur la carte pilote : **mêmes critères que l'onglet
 * « Général » du panneau Avance/Retard** (P2-11) — un véhicule est en avance ou
 * en retard significatif si son écart est ≤ `generalTabEarlySeconds` (négatif)
 * OU ≥ `generalTabLateSeconds`. Purement frontend, aucun calcul serveur touché.
 *
 * S'applique à `Journey.deviationSeconds` — un écart à l'horaire au sens
 * strict, jamais ambigu. Pour un `Deviation` (type d'écart `kind` différent
 * possible — voir `isInDeviationScope` ci-dessous), ne PAS appliquer ceci
 * directement sur `deviationSeconds` sans d'abord regarder `kind`.
 */
export function isOutOfTolerance(
  deviationSeconds: number,
  thresholds: Pick<Thresholds, "generalTabEarlySeconds" | "generalTabLateSeconds">,
): boolean {
  return (
    deviationSeconds <= thresholds.generalTabEarlySeconds ||
    deviationSeconds >= thresholds.generalTabLateSeconds
  );
}

/**
 * Un `Deviation` appartient-il au panneau Avance/Retard (CDC §5.5) ? Logique
 * PARTAGÉE par `cockpit/DeviationQueue.tsx` et `map/MapDeviationPanel.tsx` —
 * les deux appliquaient jusqu'ici le seuil `deviationSeconds` à TOUT `kind`
 * sans distinction (signalé par Claude-2, v0.217.0) :
 *  - `late` / `early` : le vrai écart à l'horaire — comparé aux seuils.
 *  - `bunching` : `deviationSeconds` porte l'**intervalle** entre les deux
 *    véhicules, pas un écart à l'horaire. Comparé aux seuils retard/avance,
 *    un intervalle de 8 min sur une ligne peu fréquente s'affichait comme
 *    « retard de 8 min » — et à l'inverse, un paquet bien réel (intervalle
 *    court, souvent sous le seuil) restait invisible. Le moteur ne détecte un
 *    bunching qu'une fois la situation établie (CDC §5.8-like cumul de
 *    preuves côté anticipation) : pas de seuil à lui appliquer ici, montré dès
 *    qu'il est émis ;
 *  - `releve-risk` : écart **autonome** posé par le moteur dès qu'une relève ou
 *    une pause légale est menacée par le retard — même sans retard « classant »
 *    (CDC-adjacent, contrat v1.23 §12.13 ; demande utilisateur 2026-10-02 et
 *    déjà demandé par Claude-2 à la livraison du contrat, jamais câblé côté
 *    front depuis). Le seuil retard/avance n'a pas de sens à lui appliquer non
 *    plus : le moteur a déjà tranché, montré dès qu'il est émis — voir
 *    `cockpit/bits.tsx` (`ReleveRiskBadge`) pour l'affichage (pastille rouge
 *    si `legalBreakAtRisk`, sinon violette).
 *  - `stale` / `no-realtime` : hors définition de ce panneau (§5.5 = avances/
 *    retards) — vivent dans le bandeau « Sources dégradées ».
 */
export function isInDeviationScope(
  deviation: Pick<Deviation, "kind" | "deviationSeconds">,
  thresholds: Pick<Thresholds, "generalTabEarlySeconds" | "generalTabLateSeconds">,
): boolean {
  switch (deviation.kind as DeviationKind) {
    case "late":
    case "early":
      return isOutOfTolerance(deviation.deviationSeconds, thresholds);
    case "bunching":
    case "releve-risk":
      return true;
    case "stale":
    case "no-realtime":
      return false;
  }
}
